/**
 * Decodes a JWT token and returns the payload
 * @param token - The JWT token string
 * @returns The decoded token payload or null if invalid
 */
export function decodeJWT(token: string): any {
  try {
    // JWT has 3 parts separated by dots: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT token format");
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    if (!payload) {
      console.error("Missing payload in JWT token");
      return null;
    }

    // Replace URL-safe characters and add padding if needed
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    // Decode base64
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
}

/**
 * Extracts roles from a decoded JWT token payload
 * @param tokenPayload - The decoded JWT token payload
 * @returns Array of role strings
 */
export function extractRolesFromToken(tokenPayload: any): string[] {
  if (!tokenPayload) {
    return [];
  }

  // Common JWT claims for roles (check multiple possible fields)
  const roles =
    tokenPayload.roles ||
    tokenPayload.role ||
    tokenPayload.groups ||
    tokenPayload.group ||
    tokenPayload[
      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
    ] ||
    [];

  // Ensure we return an array
  if (Array.isArray(roles)) {
    return roles;
  } else if (typeof roles === "string") {
    return [roles];
  }

  return [];
}

/**
 * Checks if the token is expired
 * @param tokenPayload - The decoded JWT token payload
 * @returns true if token is expired, false otherwise
 */
export function isTokenExpired(tokenPayload: any): boolean {
  if (!tokenPayload || !tokenPayload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = tokenPayload.exp * 1000;
  return Date.now() >= expirationTime;
}
